import { useAccessiblePayerCustomerIds } from '@folio/hooks';
import { useAssertedParams } from '@folio/lib';
import { buildRulePayers } from '@folio/utils/buildRulePayers';
import { usePolicyOnDashboard } from '@folio/utils/usePolicyOnDashboard';
import { usePolicyProductConfig } from '@folio/utils/usePolicyProductConfig';
import { useQueries, useQuery } from '@tanstack/react-query';
import {
  policyControllerGetPolicyQueryOptions,
  customerControllerGetCustomerQueryOptions,
  CustomerDto,
  keyValueFieldsToObject,
  customerControllerGetCustomerPaymentMethodsQueryOptions,
  GetCustomerPaymentMethodsOutput,
  viewSpecificControllerListPolicyComplianceAlertsQueryOptions,
  Customer,
  PaymentMethodDtoWithIsPreferred,
  FinalizeNewBusinessModuleRule,
} from 'korint-sdk';
import { useTranslation } from 'react-i18next';
import { useFeatureFlaggedRules } from './useFeatureFlaggedRules';

export function useFinalizeNewBusinessRules({
  additionnalRules,
  overridenBranchId,
}: {
  additionnalRules?: FinalizeNewBusinessModuleRule[];
  overridenBranchId?: string;
} = {}): { isValid: boolean; label: string; message: string; link?: string }[] | null {
  const { t } = useTranslation();

  const { rules: configuredRules } = usePolicyProductConfig('finalizeNewBusinessModuleConfig');
  const rulesConfig = useFeatureFlaggedRules(configuredRules ?? []);

  const { policyId, branchId: assertedBranchId } = useAssertedParams();
  const branchId = overridenBranchId ?? assertedBranchId;

  const policyOnDashboard = usePolicyOnDashboard();

  const { data: policyOnBranch } = useQuery({
    ...policyControllerGetPolicyQueryOptions(policyId, { branchId }),
    select: data => ({
      ...data,
      customFields: keyValueFieldsToObject(data.customFields ?? []),
    }),
    enabled: Boolean(branchId),
  });

  const policy = policyOnBranch ?? policyOnDashboard.policy;

  const customers = useQueries({
    queries: policy.customers.map(customerId => ({
      ...customerControllerGetCustomerQueryOptions(customerId, { branchId }),
      select: (data: CustomerDto) => ({
        ...data,
        customFields: keyValueFieldsToObject(data.customFields ?? []),
      }),
    })),
  });

  const paymentMethods = useQueries({
    queries: policy.customers.map(customerId => ({
      ...customerControllerGetCustomerPaymentMethodsQueryOptions(customerId, { branchId }),
      select: (data: GetCustomerPaymentMethodsOutput) => data.data,
      enabled: policy.status !== 'POLICY_CREATED',
    })),
  });

  const { data: hasUnacknowledgedAlerts } = useQuery({
    ...viewSpecificControllerListPolicyComplianceAlertsQueryOptions(policy.id),
    select: data => data.data.some(({ acknowledged }) => !acknowledged),
  });

  const payerCustomerIds = useAccessiblePayerCustomerIds(policy);

  if (!policyOnBranch || !customers[0]?.data) {
    return null;
  }

  const allCustomers = customers.map(customer => customer.data).filter(Boolean) as Customer[];
  const allPaymentMethods = paymentMethods
    .flatMap(paymentMethod => paymentMethod.data)
    .filter(Boolean) as PaymentMethodDtoWithIsPreferred[];

  const payers = buildRulePayers({
    customerIds: policy.customers,
    payerCustomerIds,
    customers,
    paymentMethods,
  });

  return [...rulesConfig, ...(additionnalRules ?? [])].map(({ id, isValid, link }) => ({
    label: t(`finalizeNewBusiness.rules.${id}.label`),
    message: t(`finalizeNewBusiness.rules.${id}.message`),
    link:
      typeof link === 'function'
        ? link({ policyId: policy.id, branchId, customerId: allCustomers[0].id })
        : link,
    isValid:
      payers !== undefined &&
      isValid({
        policy,
        customers: allCustomers,
        hasUnacknowledgedAlerts: Boolean(hasUnacknowledgedAlerts),
        customer: allCustomers[0],
        paymentMethods: allPaymentMethods,
        documents: [],
        payers,
      }),
  }));
}
